/**
 * ExportMyDataModal — one door to the member's own data.
 *
 * There used to be two Settings rows for the same thing: a readable record and a JSON download.
 * A member does not have two kinds of personal data, so this is one entry point offering the
 * three things they might actually want: read it, keep it, or take the structured file.
 *
 * "View my record" opens the record itself. It never hands the member an .html file — a member
 * should never need to know what that is.
 */

import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Printer, FileJson, Loader2, X, FileText } from 'lucide-react';
import { deliverDownload, isIOSDevice } from '../utils/deliverDownload';
import { fetchWithSession } from '../utils/sessionRefresh';

const ExportMyDataModal = ({ api, lang = 'en', onClose, onDownloadJson, isDownloadingJson }) => {
  const [pdfState, setPdfState] = useState('idle');
  const [notice, setNotice] = useState(null);

  const es = lang === 'es';

  const [recordHtml, setRecordHtml] = useState(null);
  const [recordState, setRecordState] = useState('idle');
  const frameRef = useRef(null);

  const showRecordHere = async () => {
    setRecordState('working');
    setNotice(null);
    try {
      const response = await fetchWithSession(`${api}/export/member-data/readable`);
      if (!response.ok) throw new Error(`record ${response.status}`);
      setRecordHtml(await response.text());
    } catch (err) {
      setNotice(es ? 'No se pudo abrir tu registro. Inténtalo de nuevo.' : "Your record didn't open. Try again.");
    } finally {
      setRecordState('idle');
    }
  };

  // Shown here, with the app's own session. A new window opened in Safari (always, from the
  // iPhone app) without that session and answered "Not authenticated".
  const viewRecord = () => showRecordHere();

  const saveAsPdf = async () => {
    setPdfState('working');
    setNotice(null);
    let blob;
    try {
      const response = await fetchWithSession(`${api}/export/member-data/pdf`);
      if (!response.ok) throw new Error(`pdf ${response.status}`);
      blob = await response.blob();
    } catch (err) {
      // If the PDF cannot be made on the server, show the record so the phone's own print sheet
      // can make it. (A window opened after waiting is blocked by Safari, so it opens here.)
      setPdfState('idle');
      setNotice(es
        ? 'No se pudo crear el PDF. Tu registro se abre aquí para imprimirlo o guardarlo.'
        : "The PDF couldn't be made. Your record opens here so you can print or save it.");
      showRecordHere();
      return;
    }
    try {
      const result = await deliverDownload(
        blob, `arvexis-record-${new Date().toISOString().split('T')[0]}.pdf`, 'application/pdf');
      if (result === 'downloaded' && isIOSDevice()) {
        setNotice(es
          ? 'Tu PDF está listo. Si Safari pregunta, toca Descargar: lo encontrarás en Archivos › Descargas.'
          : 'Your PDF is ready. If Safari asks, tap Download — it will be in Files › Downloads.');
      }
    } catch (err) {
      setNotice(es ? 'No se pudo guardar el PDF. Inténtalo de nuevo.' : "The PDF couldn't be saved. Try again.");
    } finally {
      setPdfState('idle');
    }
  };

  const Choice = ({ icon: Icon, title, body, onClick, testId, busy }) => (
    <button
      onClick={onClick}
      disabled={busy}
      data-testid={testId}
      style={{
        display: 'flex', gap: '14px', alignItems: 'flex-start', width: '100%',
        textAlign: 'left', padding: '16px', borderRadius: '14px',
        background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(244,239,231,0.09)',
        marginBottom: '10px', cursor: busy ? 'default' : 'pointer',
        opacity: busy ? 0.6 : 1, transition: 'background-color .2s ease, border-color .2s ease'
      }}
    >
      <span style={{ marginTop: '2px', color: '#E8C58F', flexShrink: 0 }}>
        {busy ? <Loader2 size={20} className="animate-spin" /> : <Icon size={20} />}
      </span>
      <span>
        <span style={{ display: 'block', fontSize: '15.5px', fontWeight: 500, color: '#F4EFE7' }}>
          {title}
        </span>
        <span style={{ display: 'block', fontSize: '13px', lineHeight: 1.55,
                       color: 'rgba(244, 239, 231, 0.56)', marginTop: '2px' }}>
          {body}
        </span>
      </span>
    </button>
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(7, 8, 13, 0.92)' }}
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 18 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 18 }}
        className="utility-modal"
        style={{ padding: '26px 20px', maxWidth: '400px', width: '100%' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="export-my-data-modal"
      >
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} data-testid="export-modal-close"
                  style={{ color: 'rgba(244,239,231,0.4)', padding: '2px' }}>
            <X size={18} />
          </button>
        </div>
        <div className="utility-modal__medallion">
          <FileText size={26} strokeWidth={1.4} />
        </div>
        <h3 className="utility-modal__title">{es ? 'Mis datos' : 'My data'}</h3>
        <p className="utility-modal__sub">
          {es
            ? 'Todo lo que Arvexis guarda sobre ti. Léelo, guárdalo, o llévate el archivo.'
            : 'Everything Arvexis keeps about you. Read it, keep it, or take the file.'}
        </p>

        <Choice
          icon={BookOpen}
          testId="export-view-record-btn"
          busy={recordState === 'working'}
          onClick={viewRecord}
          title={es ? 'Ver mi registro' : 'View my record'}
          body={es
            ? 'Tu historia con Arvexis, escrita para leerse. Se abre aquí mismo.'
            : 'Your history with Arvexis, written to be read. Opens right here.'}
        />
        <Choice
          icon={Printer}
          testId="export-save-pdf-btn"
          busy={pdfState === 'working'}
          onClick={saveAsPdf}
          title={pdfState === 'working'
            ? (es ? 'Preparando tu PDF…' : 'Preparing your PDF…')
            : (es ? 'Guardar como PDF' : 'Save as PDF')}
          body={es
            ? 'El mismo registro, para guardar o imprimir.'
            : 'The same record, to keep or print.'}
        />
        <Choice
          icon={FileJson}
          testId="export-download-json-btn"
          busy={isDownloadingJson}
          onClick={onDownloadJson}
          title={isDownloadingJson
            ? (es ? 'Preparando…' : 'Preparing…')
            : (es ? 'Descargar JSON' : 'Download JSON')}
          body={es
            ? 'El archivo estructurado, para llevarlo a otro servicio. No es la versión para leer.'
            : 'The structured file, for taking your data elsewhere. Not the reading version.'}
        />

        {notice && (
          <p data-testid="export-modal-notice"
             style={{ fontSize: '13px', color: 'rgba(244, 239, 231, 0.56)', marginTop: '14px' }}>
            {notice}
          </p>
        )}
      </motion.div>
      {recordHtml && (
        <div className="fixed inset-0 flex flex-col" style={{ zIndex: 60, background: '#07080D' }}
             data-testid="export-record-viewer" onClick={(e) => e.stopPropagation()}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: 'calc(env(safe-area-inset-top, 0px) + 12px) 16px 12px',
                        borderBottom: '1px solid rgba(244,239,231,0.09)' }}>
            <button onClick={() => frameRef.current?.contentWindow?.print()}
                    data-testid="export-record-print-btn"
                    style={{ color: '#E8C58F', fontSize: '14px', display: 'flex', gap: '6px', alignItems: 'center' }}>
              <Printer size={16} /> {es ? 'Imprimir' : 'Print'}
            </button>
            <button onClick={() => setRecordHtml(null)} data-testid="export-record-close-btn"
                    style={{ color: 'rgba(244,239,231,0.7)', fontSize: '14px' }}>
              {es ? 'Cerrar' : 'Close'}
            </button>
          </div>
          <iframe ref={frameRef} title={es ? 'Mi registro' : 'My record'} srcDoc={recordHtml}
                  sandbox="allow-same-origin allow-modals" data-testid="export-record-frame"
                  style={{ flex: 1, width: '100%', border: 0, background: '#fff' }} />
        </div>
      )}
    </motion.div>
  );
};

export default ExportMyDataModal;
